import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { ErrorAlert, icons, PadContainer } from 'smartsuite-ui';
import EditorDrawer from './EditorDrawer';
import EditorGrid from './EditorGrid';
import {
    customDashboardModes,
    editorMinWidgetCols,
    editorMinWidgetRows,
    editorTabs,
} from '../shared/constants/constants';
import DeleteCustomDashboardModal from './DeleteCustomDashboardModal';
import CustomDashboardHeader from './CustomDashboardHeader';
import { buildCustomDashboardRollupText } from '../shared/filtering/rollupTextUtils';
import { useLabelContext } from '../shared/hooks/labelContext';
import { areTimeAndPrecisionFieldsValid } from '../shared/filtering/filtersModalUtils';
import CustomDashboardCriteria from './CustomDashboardCriteria';
import useBeforeUnload from '../shared/hooks/useBeforeUnload';

function getWidgetPosition(widget) {
    return {
        i: widget.id,
        x: widget.stateData.position.column - 1,
        y: widget.stateData.position.row - 1,
        w: widget.stateData.position.columnSpan,
        h: widget.stateData.position.rowSpan,
        minW: editorMinWidgetCols,
        minH: editorMinWidgetRows,
    };
}

function layoutHasChanged(widgets, newLayout) {
    return widgets.some(
        // eslint-disable-next-line complexity
        (widget) => {
            const position = getWidgetPosition(widget);
            const layoutItem = newLayout.find((x) => x.i === position.i);
            return (
                !layoutItem ||
                position.x !== layoutItem.x ||
                position.y !== layoutItem.y ||
                position.w !== layoutItem.w ||
                position.h !== layoutItem.h
            );
        }
    );
}

// eslint-disable-next-line complexity
export default function Editor(props) {
    const { dashboardLabels, interpolateString } = useLabelContext();

    const [dashboardName, setDashboardName] = useState(
        props.dashboardConfiguration.title
    );
    const [gridLayout, setGridLayout] = useState(
        props.dashboardConfiguration.widgets.map(getWidgetPosition)
    );
    const [updatedWidgets, setUpdatedWidgets] = useState(
        props.dashboardConfiguration.widgets
    );
    const [selectedWidgetId, setSelectedWidgetId] = useState(
        props.selectedWidgetId
    );
    const [activeTab, setActiveTab] = useState(
        props.selectedWidgetId
            ? editorTabs.editWidget
            : editorTabs.widgetLibrary
    );
    const [draggingWidget, setDraggingWidget] = useState();
    const [drawerCollapsed, setDrawerCollapsed] = useState(false);
    const [errorWidgetIds, setErrorWidgetIds] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [dashboardTimeAndPrecision, setDashboardTimeAndPrecision] = useState(
        props.dashboardConfiguration.timeAndPrecision
    );
    const [isCriteriaCollapsed, setIsCriteriaCollapsed] = useState(false);
    const [overrideUnload, setOverrideUnload] = useState(false);

    const hasPendingChanges =
        props.hasPendingChanges ||
        updatedWidgets !== props.dashboardConfiguration.widgets ||
        dashboardName !== props.dashboardConfiguration.title ||
        dashboardTimeAndPrecision !==
            props.dashboardConfiguration.timeAndPrecision ||
        layoutHasChanged(props.dashboardConfiguration.widgets, gridLayout);

    const timeAndPrecisionFieldsAreValid = areTimeAndPrecisionFieldsValid(
        dashboardTimeAndPrecision,
        {},
        true,
        interpolateString,
        dashboardLabels
    );

    const hasErrors =
        errorWidgetIds.length > 0 ||
        !timeAndPrecisionFieldsAreValid ||
        !dashboardName;

    // prettier-ignore
    const rollupText = timeAndPrecisionFieldsAreValid
        ? buildCustomDashboardRollupText(
            dashboardTimeAndPrecision,
            dashboardLabels
        )
        : dashboardLabels.invalidDashboardTimeRange;

    useBeforeUnload(hasPendingChanges, props.isCreateMode, overrideUnload);

    function getLatestConfiguration() {
        const widgets = updatedWidgets.reduce((result, widget) => {
            const layoutItem = gridLayout.find((x) => x.i === widget.id);
            if (layoutItem) {
                const position = {
                    row: layoutItem.y + 1,
                    column: layoutItem.x + 1,
                    rowSpan: layoutItem.h,
                    columnSpan: layoutItem.w,
                };
                result.push({
                    ...widget,
                    stateData: {
                        ...widget.stateData,
                        position,
                    },
                });
            }
            return result;
        }, []);

        return {
            title: dashboardName,
            timeAndPrecision: dashboardTimeAndPrecision,
            widgets,
        };
    }

    function updateWidget(widgetProperty) {
        setUpdatedWidgets((prevState) =>
            prevState.map((widget) => {
                if (widget.id !== selectedWidgetId) {
                    return widget;
                }

                return {
                    ...widget,
                    stateData: { ...widget.stateData, ...widgetProperty },
                };
            })
        );
    }

    function toggleDrawer() {
        setDrawerCollapsed(!drawerCollapsed);
    }

    const setNewActiveTab = useCallback(
        (newActiveTab) => setActiveTab(newActiveTab),
        []
    );

    const deleteWidgetById = useCallback(
        (widgetId) => {
            setUpdatedWidgets((prevState) => {
                const widget = prevState.find((x) => x.id === widgetId);
                return prevState.filter((x) => x.id !== widget.id);
            });
            setGridLayout((prevState) =>
                prevState.filter((x) => x.i !== widgetId)
            );
            setNewActiveTab(editorTabs.widgetLibrary, widgetId);
            setErrorWidgetIds((prevState) =>
                prevState.filter((x) => x !== widgetId)
            );
        },
        [setNewActiveTab]
    );

    const deleteSelectedWidget = useCallback(() => {
        deleteWidgetById(selectedWidgetId);
    }, [deleteWidgetById, selectedWidgetId]);

    function onAdd(widget) {
        setUpdatedWidgets((prevState) => [...prevState, widget]);
        setGridLayout((prevState) => [...prevState, widget]);
        setSelectedWidgetId(widget.id);
        setNewActiveTab(editorTabs.editWidget);
    }

    const pageActions = [];

    if (props.dashboardKey) {
        pageActions.push({
            iconClass: icons.trash,
            sortOrder: 1,
            onClick: () => setShowDeleteModal(true),
        });
    }

    return (
        <div className="dashboard">
            {props.errorMessage && (
                <ErrorAlert>{props.errorMessage}</ErrorAlert>
            )}
            <CustomDashboardHeader
                dashboardHasErrors={hasErrors}
                isMetricDataLoading={false}
                isModified={!props.isCreateMode && hasPendingChanges}
                mode={customDashboardModes.edit}
                onlineHelpId={props.dashboardConfiguration.onlineHelpId}
                pageActions={pageActions}
                toggleView={() =>
                    props.navigateToCustomDashboard(
                        getLatestConfiguration(),
                        props.dashboardKey,
                        hasPendingChanges
                    )
                }
                title={
                    props.dashboardConfiguration.title ||
                    dashboardLabels.newDashboard
                }
            />
            <PadContainer
                cssClassModifiers={['pad-medium', 'no-pad-left-right']}
            >
                <CustomDashboardCriteria
                    dashboardName={dashboardName}
                    dashboardTimeAndPrecision={dashboardTimeAndPrecision}
                    hasErrors={hasErrors}
                    hasPendingChanges={hasPendingChanges}
                    isCollapsed={isCriteriaCollapsed}
                    isCreateMode={props.isCreateMode}
                    onCollapse={() =>
                        setIsCriteriaCollapsed(!isCriteriaCollapsed)
                    }
                    rollupText={rollupText}
                    saveDashboard={() => {
                        setOverrideUnload(true);
                        props.saveDashboard(getLatestConfiguration());
                    }}
                    setDashboardName={setDashboardName}
                    setDashboardTimeAndPrecision={setDashboardTimeAndPrecision}
                />
            </PadContainer>
            <div className="editor__content">
                <EditorGrid
                    deleteWidget={deleteWidgetById}
                    draggingWidget={draggingWidget}
                    drawerCollapsed={drawerCollapsed}
                    errorWidgetIds={errorWidgetIds}
                    gridLayout={gridLayout}
                    selectedWidgetId={selectedWidgetId}
                    setActiveTab={setNewActiveTab}
                    setDraggingWidget={setDraggingWidget}
                    setGridLayout={setGridLayout}
                    setSelectedWidgetId={setSelectedWidgetId}
                    setUpdatedWidgets={setUpdatedWidgets}
                    widgets={updatedWidgets}
                />
                <EditorDrawer
                    activeTab={activeTab}
                    availableWidgets={props.availableWidgets}
                    dashboardTimeAndPrecision={dashboardTimeAndPrecision}
                    deleteWidget={deleteSelectedWidget}
                    drawerCollapsed={drawerCollapsed}
                    gridLayout={gridLayout}
                    onAdd={onAdd}
                    selectedWidget={updatedWidgets.find(
                        (x) => x.id === selectedWidgetId
                    )}
                    setActiveTab={setNewActiveTab}
                    setDraggingWidget={setDraggingWidget}
                    setErrorWidgetIds={setErrorWidgetIds}
                    toggleDrawer={toggleDrawer}
                    updateWidget={updateWidget}
                />
            </div>
            {showDeleteModal && (
                <DeleteCustomDashboardModal
                    dashboardKey={props.dashboardKey}
                    deleteDashboard={() => {
                        setOverrideUnload(true);
                        props.deleteDashboard();
                    }}
                    onDismiss={() => setShowDeleteModal(false)}
                />
            )}
        </div>
    );
}

Editor.propTypes = {
    availableWidgets: PropTypes.instanceOf(Array).isRequired,
    dashboardConfiguration: PropTypes.instanceOf(Object).isRequired,
    dashboardKey: PropTypes.string,
    deleteDashboard: PropTypes.func,
    hasPendingChanges: PropTypes.bool,
    isCreateMode: PropTypes.bool,
    errorMessage: PropTypes.string,
    navigateToCustomDashboard: PropTypes.func.isRequired,
    saveDashboard: PropTypes.func.isRequired,
    selectedWidgetId: PropTypes.string,
};

Editor.defaultProps = {
    deleteDashboard: () => {},
};
